import { useEffect, useState } from "react";
import {
  getHospitalIntegrationStatus,
  getPatientHistory,
  linkHospitalPatient,
  searchHospitalPatients,
  searchPatients,
} from "../api";
import { callPurposeLabel } from "../callPurpose";
import {
  isPatientSearchDateRangeValid,
  isPatientSearchQueryValid,
  normalizePatientSearchQuery,
} from "../patientSearch";
import type {
  HospitalIntegrationStatus,
  HospitalPatientCandidate,
  HospitalRelationship,
  PatientHistory,
  PatientSearchResult,
} from "../types";
import { StatusBadge } from "./StatusBadge";

function thaiDateTime(value: string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("th-TH", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function patientInitial(name: string) {
  return name.trim().slice(0, 1).toUpperCase() || "•";
}

function relationshipLabel(value: HospitalRelationship) {
  return {
    SELF: "ผู้ป่วยเอง",
    RELATIVE: "ญาติ",
    CAREGIVER: "ผู้ดูแล",
    OTHER: "บุคคลอื่น",
  }[value];
}

export function PatientHistoryPanel() {
  const [query, setQuery] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [results, setResults] = useState<PatientSearchResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [history, setHistory] = useState<PatientHistory | null>(null);
  const [integration, setIntegration] = useState<HospitalIntegrationStatus | null>(null);
  const [hospitalQuery, setHospitalQuery] = useState("");
  const [relationship, setRelationship] = useState<HospitalRelationship>("SELF");
  const [hospitalResults, setHospitalResults] = useState<HospitalPatientCandidate[]>([]);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    void getHospitalIntegrationStatus()
      .then(setIntegration)
      .catch(() => setIntegration({
        configured: false,
        provider: "disabled",
        sourceSystem: "HIS",
        facilityCode: "DEFAULT",
        message: "ยังไม่ได้ Deploy hospital-api หรือยังไม่ได้ตั้งค่าการเชื่อมต่อ",
      }));
  }, []);

  function showError(error: unknown) {
    setIsError(true);
    setMessage(error instanceof Error ? error.message : "ดำเนินการไม่สำเร็จ");
  }

  async function handleSearch(event: React.FormEvent) {
    event.preventDefault();
    const normalized = normalizePatientSearchQuery(query);
    setMessage("");
    setIsError(false);
    if (!isPatientSearchQueryValid(normalized)) {
      setIsError(true);
      setMessage("กรุณากรอกชื่อ เบอร์โทร HN หรือเลขคิว อย่างน้อย 2 ตัวอักษร");
      return;
    }
    if (!isPatientSearchDateRangeValid(fromDate, toDate)) {
      setIsError(true);
      setMessage("วันที่เริ่มต้นต้องไม่อยู่หลังวันที่สิ้นสุด");
      return;
    }

    setBusy("search");
    setHistory(null);
    try {
      const patients = await searchPatients({
        query: normalized,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      setResults(patients);
      setHasSearched(true);
      if (patients.length === 0) setMessage("ไม่พบประวัติการรับคิวที่ตรงกับคำค้นหา");
    } catch (error) {
      showError(error);
    } finally {
      setBusy("");
    }
  }

  async function openHistory(customerId: string) {
    setBusy(`history:${customerId}`);
    setMessage("");
    setIsError(false);
    setHospitalResults([]);
    try {
      setHistory(await getPatientHistory(customerId));
    } catch (error) {
      showError(error);
    } finally {
      setBusy("");
    }
  }

  async function handleHospitalSearch(event: React.FormEvent) {
    event.preventDefault();
    const normalized = normalizePatientSearchQuery(hospitalQuery);
    if (!isPatientSearchQueryValid(normalized)) {
      setIsError(true);
      setMessage("กรุณากรอก HN ชื่อ หรือรหัสผู้ป่วยอย่างน้อย 2 ตัวอักษร");
      return;
    }
    setBusy("hospital-search");
    setMessage("");
    setIsError(false);
    try {
      const patients = await searchHospitalPatients(normalized);
      setHospitalResults(patients);
      if (patients.length === 0) setMessage("ไม่พบผู้ป่วยจากระบบโรงพยาบาล");
    } catch (error) {
      showError(error);
    } finally {
      setBusy("");
    }
  }

  async function handleLink(candidate: HospitalPatientCandidate) {
    if (!history) return;
    const confirmed = window.confirm(
      `ยืนยันเชื่อม ${candidate.hospitalHn ?? candidate.externalPatientId} กับ ${history.patient.customerName}?`,
    );
    if (!confirmed) return;
    setBusy(`link:${candidate.externalPatientId}`);
    setMessage("");
    setIsError(false);
    try {
      await linkHospitalPatient({
        customerId: history.patient.customerId,
        externalPatientId: candidate.externalPatientId,
        relationship,
        isPrimary: true,
      });
      setHistory(await getPatientHistory(history.patient.customerId));
      setHospitalResults([]);
      setMessage("เชื่อมข้อมูลผู้ป่วยจากระบบโรงพยาบาลสำเร็จ");
    } catch (error) {
      showError(error);
    } finally {
      setBusy("");
    }
  }

  return (
    <section className="patient-history-panel">
      <div className="patient-history-heading">
        <div>
          <small>PATIENT QUEUE HISTORY</small>
          <h2>ค้นหาและประวัติผู้ป่วย</h2>
          <p>ค้นหาประวัติการรับคิวเท่านั้น ไม่ใช่เวชระเบียนหรือประวัติการรักษา</p>
        </div>
        <div className={`integration-status${integration?.configured ? " integration-status--ready" : ""}`}>
          <i aria-hidden="true" />
          <span>
            <b>{integration?.configured ? "เชื่อมต่อ HIS พร้อมใช้งาน" : "ยังไม่เชื่อมต่อ HIS"}</b>
            <small>{integration?.message ?? "กำลังตรวจสอบ…"}</small>
          </span>
        </div>
      </div>

      <form className="patient-search-form" onSubmit={handleSearch}>
        <label className="patient-search-form__query">
          คำค้นหา
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="ชื่อ เบอร์โทร HN001 หรือ HN โรงพยาบาล"
            maxLength={100}
          />
        </label>
        <label>
          ตั้งแต่วันที่
          <input type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
        </label>
        <label>
          ถึงวันที่
          <input type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
        </label>
        <button className="primary-button primary-button--small" disabled={busy !== ""}>
          {busy === "search" ? "กำลังค้นหา…" : "ค้นหา"}
        </button>
      </form>

      {message && (
        <div className={`patient-message${isError ? " patient-message--error" : ""}`} role="status">
          {message}
        </div>
      )}

      <div className="patient-history-layout">
        <div className="patient-search-results">
          <div className="patient-section-title">
            <h3>ผลการค้นหา</h3>
            <span>{results.length} รายการ</span>
          </div>
          {!hasSearched ? (
            <div className="patient-placeholder">กรอกข้อมูลด้านบนเพื่อค้นหาประวัติข้ามวัน</div>
          ) : results.length === 0 ? (
            <div className="patient-placeholder">ไม่พบข้อมูล</div>
          ) : (
            results.map((patient) => (
              <button
                type="button"
                className={`patient-result${history?.patient.customerId === patient.customerId ? " active" : ""}`}
                key={patient.customerId}
                disabled={busy !== ""}
                onClick={() => void openHistory(patient.customerId)}
              >
                <span className="patient-avatar">{patientInitial(patient.customerName)}</span>
                <span className="patient-result__body">
                  <strong>{patient.hospitalPatient?.displayName ?? patient.customerName}</strong>
                  <small>{patient.customerPhoneNumber ?? "ไม่มีเบอร์โทร"}</small>
                  <small>
                    {patient.hospitalPatient?.hospitalHn
                      ? `HN โรงพยาบาล: ${patient.hospitalPatient.hospitalHn}`
                      : "ยังไม่เชื่อมรหัสผู้ป่วยโรงพยาบาล"}
                  </small>
                </span>
                <span className="patient-result__meta">
                  <b>{patient.totalQueues} ครั้ง</b>
                  <small>{patient.lastQueueNumber}</small>
                  <small>{thaiDateTime(patient.lastQueueAt)}</small>
                </span>
              </button>
            ))
          )}
        </div>

        <div className="patient-history-detail">
          {!history ? (
            <div className="patient-placeholder patient-placeholder--large">
              เลือกรายชื่อเพื่อดูประวัติการรับคิวและข้อมูลเชื่อมต่อโรงพยาบาล
            </div>
          ) : (
            <>
              <div className="patient-profile">
                <span className="patient-avatar patient-avatar--large">
                  {patientInitial(history.patient.customerName)}
                </span>
                <div>
                  <small>ผู้รับข้อความ LINE</small>
                  <h3>{history.patient.customerName}</h3>
                  <p>{history.patient.customerPhoneNumber ?? "ไม่มีเบอร์โทรศัพท์"}</p>
                </div>
              </div>

              <div className="hospital-link-block">
                <div className="patient-section-title">
                  <h3>ข้อมูลจากโรงพยาบาล</h3>
                  <span>{history.patient.hospitalPatients.length > 0 ? "เชื่อมแล้ว" : "ยังไม่เชื่อม"}</span>
                </div>
                {history.patient.hospitalPatients.map((patient) => (
                  <div className="hospital-linked-patient" key={patient.id}>
                    <div>
                      <strong>{patient.displayName ?? "ไม่ระบุชื่อ"}</strong>
                      <span>HN: {patient.hospitalHn ?? "—"}</span>
                    </div>
                    <small>{patient.sourceSystem} · {patient.facilityCode} · {relationshipLabel(patient.relationship)}</small>
                  </div>
                ))}
                {integration?.configured ? (
                  <>
                    <form className="hospital-search-form" onSubmit={handleHospitalSearch}>
                      <input
                        value={hospitalQuery}
                        onChange={(event) => setHospitalQuery(event.target.value)}
                        placeholder="ค้นหา HN หรือชื่อจาก HIS"
                        maxLength={100}
                      />
                      <button className="secondary-button" disabled={busy !== ""}>
                        {busy === "hospital-search" ? "กำลังค้นหา…" : "ค้นหาจาก HIS"}
                      </button>
                    </form>
                    <label className="hospital-relationship-field">
                      ผู้ใช้ LINE รายนี้เป็น
                      <select
                        value={relationship}
                        onChange={(event) => setRelationship(event.target.value as HospitalRelationship)}
                      >
                        <option value="SELF">ผู้ป่วยเอง</option>
                        <option value="RELATIVE">ญาติ</option>
                        <option value="CAREGIVER">ผู้ดูแล</option>
                        <option value="OTHER">บุคคลอื่น</option>
                      </select>
                    </label>
                    <div className="hospital-candidates">
                      {hospitalResults.map((candidate) => (
                        <div key={candidate.externalPatientId}>
                          <span>
                            <strong>{candidate.displayName ?? "ไม่ระบุชื่อ"}</strong>
                            <small>HN: {candidate.hospitalHn ?? "—"} · {candidate.phoneNumber ?? "ไม่มีเบอร์"}</small>
                          </span>
                          <button
                            className="secondary-button"
                            disabled={busy !== ""}
                            onClick={() => void handleLink(candidate)}
                          >
                            {busy === `link:${candidate.externalPatientId}` ? "กำลังเชื่อม…" : "เชื่อมข้อมูล"}
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="hospital-not-configured">
                    หน้านี้พร้อมรองรับ Hospital Integration Gateway แล้ว เมื่อมี API ของโรงพยาบาลจึงเปิดการค้นหาและเชื่อม HN ได้
                  </p>
                )}
              </div>

              <div className="queue-history-block">
                <div className="patient-section-title">
                  <h3>ประวัติการรับคิว</h3>
                  <span>ล่าสุด {history.queues.length} รายการ</span>
                </div>
                <div className="queue-history-list">
                  {history.queues.map((queue) => (
                    <article key={queue.id}>
                      <div className="queue-history-number">
                        <strong>{queue.queueNumber}</strong>
                        <small>{new Intl.DateTimeFormat("th-TH", { dateStyle: "medium" }).format(new Date(`${queue.queueDate}T12:00:00`))}</small>
                      </div>
                      <div>
                        <StatusBadge status={queue.status} />
                        <p>{queue.callPurpose ? callPurposeLabel(queue.callPurpose) : "ยังไม่ได้เรียก"}</p>
                      </div>
                      <div className="queue-history-times">
                        <span>รับคิว {thaiDateTime(queue.createdAt)}</span>
                        <span>เรียก {thaiDateTime(queue.calledAt)}</span>
                        <span>เสร็จ {thaiDateTime(queue.completedAt ?? queue.cancelledAt)}</span>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
